import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtHM } from '../../lib/formatHelpers';

/**
 * V2 CreatorDetailActivity — Activité agrégée + timeline des derniers snapshots.
 *
 * Props :
 *  - activity   : { sessions, hours, days, validGoLiveDays, matches, newFollowers }
 *  - dailyRows  : array<{ snapshot_date, live_streams, live_duration_seconds,
 *                         valid_go_live_days, matches, new_followers }>
 *  - lookbackDays
 *  - loading
 */
const Cell = ({ label, value }) => (
  <div className="rounded-xl bg-background border border-border p-3 min-w-0">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold truncate">{label}</div>
    <div className="text-xl md:text-2xl font-heading font-bold text-foreground tabular-nums mt-1 truncate">{value}</div>
  </div>
);

const CreatorDetailActivity = ({ activity = {}, dailyRows = [], lookbackDays = 60, loading = false }) => {
  const { t } = useTranslation();

  // Recent 10 days timeline
  const recent = useMemo(() => {
    return [...dailyRows]
      .sort((a, b) => (b.snapshot_date || '').localeCompare(a.snapshot_date || ''))
      .slice(0, 10);
  }, [dailyRows]);

  return (
    <section className="space-y-4">
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-semibold text-foreground text-sm">
            {t('creatorsV2.detail.activityTitle', 'Activity')}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">
            {t('creatorsV2.detail.lookback', '{{n}}-day lookback', { n: lookbackDays })}
          </span>
        </div>
        {loading ? (
          <div className="h-24 skeleton rounded" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Cell label={t('creatorsV2.detail.sessions', 'Sessions')} value={fmtNum(activity.sessions)} />
            <Cell label={t('creatorsV2.detail.hours', 'Live hours')} value={fmtHM((activity.hours || 0) * 3600)} />
            <Cell label={t('creatorsV2.detail.days', 'Live days')} value={fmtNum(activity.days)} />
            <Cell label={t('creatorsV2.detail.validDays', 'Valid days')} value={fmtNum(activity.validGoLiveDays)} />
            <Cell label={t('creatorsV2.detail.matches', 'Matches')} value={fmtNum(activity.matches)} />
            <Cell label={t('creatorsV2.detail.newFollowers', 'New followers')} value={fmtNum(activity.newFollowers)} />
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-card border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-surface-2 font-heading font-semibold text-foreground text-sm">
          {t('creatorsV2.detail.recentTitle', 'Recent days')}
        </div>
        {loading ? (
          <div className="h-32 skeleton" />
        ) : recent.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-muted-foreground">
            {t('creatorsV2.detail.noRecent', 'No recorded activity for this creator yet.')}
          </p>
        ) : (
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="border-b border-border">
                  <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {t('creatorsV2.detail.date', 'Date')}
                  </th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {t('creatorsV2.detail.sessions', 'Sessions')}
                  </th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {t('creatorsV2.detail.hours', 'Hours')}
                  </th>
                  <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {t('creatorsV2.detail.validDays', 'Valid')}
                  </th>
                  <th className="text-right px-5 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {t('creatorsV2.detail.matches', 'Matches')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r, i) => (
                  <tr key={i} className="border-b border-border last:border-b-0">
                    <td className="px-5 py-2 text-sm">
                      {r.snapshot_date ? new Date(r.snapshot_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.live_streams)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtHM(r.live_duration_seconds || 0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.valid_go_live_days)}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{fmtNum(r.matches)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Mobile list */}
        {recent.length > 0 && (
          <div className="md:hidden divide-y divide-border">
            {recent.map((r, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="text-xs text-foreground">
                  {r.snapshot_date ? new Date(r.snapshot_date).toLocaleDateString() : '—'}
                </div>
                <div className="text-xs tabular-nums text-muted-foreground">
                  {fmtNum(r.live_streams)} {t('creatorsV2.detail.sessionsShort', 'sess.')} · {fmtHM(r.live_duration_seconds || 0)} · {fmtNum(r.valid_go_live_days)} {t('creatorsV2.detail.validShort', 'valid')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CreatorDetailActivity;
